import { defaultGetter, defaultSetter } from 'rewire-common';
import { ActionType } from 'rewire-ui';
import { IRole, ISort } from '../DataTypes';
import DataMutations from '../graphql/Mutations';
import DataQueries from '../graphql/Queries';
import mainPageModel from '../main-navigation/MainPageModel';
import rolePermissionDialogModel, { RolePermissionDialogModel } from './RolePermissionDialogModel';
import SettingsModel from './SettingsModel';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import LogIcon from '@material-ui/icons/AssignmentOutlined';
import PagingModel from './PagingModel';
import PrioritySortMenuModel from '../ui/PrioritySortMenuModel';
import auditLogDialogModel from '../audit/AuditLogDialogModel';
import { IAuditLogFilters } from '../audit/AuditLogListModel';
import RolePermissionSettingsEditPanelView from './RolePermissionSettingsEditPanelView';
import RolePermissionSettingsItemsPanelView from './RolePermissionSettingsItemsPanelView';

const RoleSchema = `
  id
  name
  code
  isSystem
`;

export default class RolesSettingsModel extends SettingsModel {
  constructor() {
    super(
      'Roles',
      'roles',
      rolePermissionDialogModel,
      [{ label: 'Code', get: defaultGetter('code'), set: defaultSetter('code') }],
      undefined,
      DataMutations.updateRoles,
    );

    function load(filter?: { search?: string }): Promise<{ cursor?: string; totalCount: number }> {
      return this._load(filter, { name: { direction: 'asc' } });
    }

    this.paging = new PagingModel(load.bind(this));
    this.sortModel = new PrioritySortMenuModel(
      () => this.paging.load(true),
      () => this.getSortMenuItems(),
    );
    this.ItemsPanelView = RolePermissionSettingsItemsPanelView;
    this.EditPanelView = RolePermissionSettingsEditPanelView as any;
  }

  get access(): boolean {
    return mainPageModel.rolesAccess;
  }

  get _canAddItem(): boolean {
    return mainPageModel.canAddRoles;
  }

  get _canEditItem(): boolean {
    return mainPageModel.canEditRoles;
  }

  get _canDeleteItem(): boolean {
    return mainPageModel.canDeleteRoles;
  }

  getSortMenuItems(): { name: string; title: string; isCustom?: boolean }[] {
    return [
      { name: 'code', title: 'Code' },
      { name: 'name', title: 'Name' },
    ];
  }

  protected async _load(filter: { search?: string }, sort?: ISort) {
    const results = await DataQueries.getRoles(RoleSchema, filter, sort, this.paging.itemsPerPage, this.paging.currentPage);
    this.setItems(results.data);
    return results;
  }

  get hasCheckedItems() {
    return this.items?.some((item: any) => item.checked);
  }

  duplicateItem = async () => {
    (this.editor as RolePermissionDialogModel)?.duplicateItem(this);
  };

  loadAuditLogDialog = () => {
    const filter = { types: ['role'] } as IAuditLogFilters;
    if (this.selectedItem?.id) {
      filter.entityIds = [this.selectedItem.id];
    }
    auditLogDialogModel.load(filter);
  };

  getDefaultEditPanelHeaderOptionsItems() {
    const optionsItems = super
      .getDefaultEditPanelHeaderOptionsItems()
      .filter((o) => !['deleteRoles', 'Udfs'].some((a) => a === o.name));
    const isItemSelected = !!this.selectedItem;
    const isSystemRole = isItemSelected && (this.selectedItem as IRole).isSystem;
    optionsItems.push({
      name: `delete${this.title}`,
      title: 'Delete Role',
      icon: DeleteIcon,
      onClick: this.removeItem,
      closeOnClick: true,
      disabled: () => !this._canDeleteItem || !isItemSelected || isSystemRole,
    });
    optionsItems.push({
      name: `duplicate${this.title}`,
      title: 'Duplicate Role',
      onClick: this.duplicateItem,
      closeOnClick: true,
      disabled: () => !this._canAddItem || !isItemSelected,
    });
    optionsItems.push({
      name: 'auditLog',
      title: 'View Audit Log',
      icon: LogIcon,
      onClick: this.loadAuditLogDialog,
      closeOnClick: true,
      disabled: () => !isItemSelected,
    });
    return optionsItems;
  }

  get footerActions(): { [index: string]: ActionType } | undefined {
    return { undo: this.editor!.actions.undo, save: this.editor!.actions.save };
  }
}
